// The big answer widgets of board 25 v2 (one per screen): the underline text
// field, the phone field with the country chip, six code cells, six PIN dots,
// large chips, the inline message, the celebration burst — plus the small
// helpers the screens share (countdown, clock, masked phone, role labels).

import { forwardRef, useEffect, useId, useRef, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, CircleAlert, Info, Lock, Search } from 'lucide-react';
import { sessionStore } from '@/lib/auth';
import { normalizeDigits, toArabicDigits } from '@/lib/digits';

// ------------------------------------------------------------ messages --

export type MsgTone = 'error' | 'warn' | 'info' | 'ok';

/** The inline message under a widget (error / warn / info / ok) — icon + one sentence. */
export function Msg({ tone = 'error', children, testId }: { tone?: MsgTone; children: ReactNode; testId?: string }) {
  const Icon = tone === 'warn' ? Lock : tone === 'ok' ? Check : tone === 'info' ? Info : CircleAlert;
  return (
    <div className="onb-msg" data-tone={tone} role={tone === 'error' ? 'alert' : 'status'} data-testid={testId}>
      <Icon aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

// ------------------------------------------------------------ big input --

export interface BigInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (v: string) => void;
  error?: ReactNode;
  /** Technical value: LTR + Latin font. */
  ltr?: boolean;
}

/** One large answer with an underline (name, address): 34 px (30 at 1024, 26 on a phone). */
export const BigInput = forwardRef<HTMLInputElement, BigInputProps>(function BigInput({ value, onChange, error, ltr, className, ...rest }, ref) {
  return (
    <div className="onb-big" data-invalid={error ? 'true' : undefined}>
      <div className={`onb-big-field${ltr ? ' onb-big-field--ltr' : ''}`} dir={ltr ? 'ltr' : undefined}>
        <input ref={ref} className={`onb-big-input${ltr ? ' ltr' : ''}${className ? ` ${className}` : ''}`} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} {...rest} />
      </div>
      {error && (
        <div className="onb-big-error" role="alert">
          <CircleAlert aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------- phone --

/** The ten national digits out of anything typed (07XX…, 7XX…, +964…, Arabic-Indic). */
export function phoneDigits(input: string): string {
  let d = normalizeDigits(input).replace(/\D/g, '');
  if (d.startsWith('00964')) d = d.slice(5);
  else if (d.startsWith('964')) d = d.slice(3);
  if (d.startsWith('0')) d = d.slice(1);
  return d.slice(0, 10);
}

/** «770 123 4567» as the board groups the national digits after the country chip. */
export function groupPhone(digits: string): string {
  const d = digits.slice(0, 10);
  return [d.slice(0, 3), d.slice(3, 6), d.slice(6)].filter(Boolean).join(' ');
}

export interface PhoneBigProps {
  /** National digits (no leading 0). */
  value: string;
  onChange: (digits: string) => void;
  error?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  'data-testid'?: string;
  'aria-label': string;
}

/** The big LTR phone field: the +964 chip (locales.json IQ) then the number at 34 px. */
export const PhoneBig = forwardRef<HTMLInputElement, PhoneBigProps>(function PhoneBig(
  { value, onChange, error, disabled, readOnly, autoFocus, placeholder = '770 123 4567', 'data-testid': testId, 'aria-label': ariaLabel },
  ref,
) {
  return (
    <div className="onb-big" data-invalid={error ? 'true' : undefined} data-disabled={disabled ? 'true' : undefined}>
      <div className="onb-phone" dir="ltr">
        <span className="onb-phone-cc ltr" aria-hidden="true">
          +964
          <ChevronDown />
        </span>
        <input
          ref={ref}
          className="onb-phone-input ltr"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          placeholder={placeholder}
          value={groupPhone(value)}
          onChange={(e) => onChange(phoneDigits(e.target.value))}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          data-testid={testId}
        />
      </div>
      {error && (
        <div className="onb-big-error" role="alert">
          <CircleAlert aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------- cells --

export interface CodeCellsProps {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  length?: number;
  label: string;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  'data-testid'?: string;
}

/**
 * Six cells 56 × 68 (48 × 56 on a phone), LTR: ONE real input drives them so
 * paste fills all six and autofill works; the sixth digit submits (onComplete).
 */
export const CodeCells = forwardRef<HTMLInputElement, CodeCellsProps>(function CodeCells(
  { value, onChange, onComplete, length = 6, label, invalid, disabled, autoFocus, 'data-testid': testId },
  ref,
) {
  const inner = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [shake, setShake] = useState(0);
  const prevInvalid = useRef(false);
  useEffect(() => {
    if (invalid && !prevInvalid.current) setShake((n) => n + 1);
    prevInvalid.current = !!invalid;
  }, [invalid]);
  const setRef = (el: HTMLInputElement | null) => {
    (inner as { current: HTMLInputElement | null }).current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  };
  const active = focused && !disabled ? Math.min(value.length, length - 1) : -1;
  return (
    <div className="onb-cells" dir="ltr" onClick={() => inner.current?.focus()} data-invalid={invalid ? 'true' : undefined} data-shake={shake || undefined} data-disabled={disabled ? 'true' : undefined}>
      <input
        ref={setRef}
        className="onb-cells-input"
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label={label}
        aria-invalid={invalid || undefined}
        maxLength={length * 2}
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid={testId}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const v = normalizeDigits(e.target.value).replace(/\D/g, '').slice(0, length);
          onChange(v);
          if (v.length === length && v !== value) onComplete?.(v);
        }}
      />
      {Array.from({ length }, (_, i) => (
        <span key={i} className="onb-cell" data-filled={value[i] ? 'true' : undefined} data-active={i === active ? 'true' : undefined} aria-hidden="true">
          {value[i] ?? ''}
        </span>
      ))}
    </div>
  );
});

// ----------------------------------------------------------------- dots --

export const PIN_LENGTH = 6;

export interface PinDotsProps {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  label: string;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  'data-testid'?: string;
}

/** Six 20 px dots over a hidden numeric input (board 25 د): filled = ink, empty = ring; error = the error colour. */
export const PinDots = forwardRef<HTMLInputElement, PinDotsProps>(function PinDots({ value, onChange, onComplete, label, invalid, disabled, autoFocus, 'data-testid': testId }, ref) {
  const inner = useRef<HTMLInputElement>(null);
  const [shake, setShake] = useState(0);
  const prevInvalid = useRef(false);
  useEffect(() => {
    if (invalid && !prevInvalid.current) setShake((n) => n + 1);
    prevInvalid.current = !!invalid;
  }, [invalid]);
  const setRef = (el: HTMLInputElement | null) => {
    (inner as { current: HTMLInputElement | null }).current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  };
  return (
    <div className="onb-dots" dir="ltr" onClick={() => inner.current?.focus()} data-invalid={invalid ? 'true' : undefined} data-shake={shake || undefined} data-complete={value.length === PIN_LENGTH ? 'true' : undefined}>
      <input
        ref={setRef}
        className="onb-dots-input"
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        aria-label={label}
        aria-invalid={invalid || undefined}
        maxLength={PIN_LENGTH * 2}
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid={testId}
        onChange={(e) => {
          const v = normalizeDigits(e.target.value).replace(/\D/g, '').slice(0, PIN_LENGTH);
          onChange(v);
          if (v.length === PIN_LENGTH && v !== value) onComplete?.(v);
        }}
      />
      {Array.from({ length: PIN_LENGTH }, (_, i) => (
        <span key={i} className="onb-dot" data-filled={i < value.length ? 'true' : undefined} aria-hidden="true" />
      ))}
    </div>
  );
});

// ---------------------------------------------------------------- chips --

export interface BigChipItem {
  id: string;
  label: string;
  /** The last chip opens a search / free-text field («كل الأنواع», «مدينة أخرى»). */
  search?: boolean;
}

export interface BigChipsProps {
  items: BigChipItem[];
  value: string | null;
  onChange: (id: string) => void;
  label: string;
  'data-testid'?: string;
}

/** Large chips (48 px; 44 on a phone) instead of a dropdown; selected = ink fill + check; staggered pop-in. */
export function BigChips({ items, value, onChange, label, 'data-testid': testId }: BigChipsProps) {
  return (
    <div className="onb-chips" role="group" aria-label={label} data-testid={testId}>
      {items.map((it, i) => {
        const sel = it.id === value;
        return (
          <button
            key={it.id}
            type="button"
            className="onb-chip"
            aria-pressed={sel}
            style={{ animationDelay: `${Math.min(i, 14) * 60}ms` }}
            onClick={() => onChange(it.id)}
            data-search={it.search ? 'true' : undefined}
          >
            {it.search && <Search aria-hidden="true" />}
            {it.label}
            {sel && <Check aria-hidden="true" className="onb-chip-check" />}
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------- shape note --

/** «الحجز في هذه الفئة يكون على <b>موظف</b>: … غيّره لاحقاً من الإعدادات» */
export function ShapeNote({ before, word, sentence, later, testId }: { before: string; word: string; sentence: string | null; later: string; testId?: string }) {
  return (
    <div className="onb-shape" data-testid={testId}>
      <Check aria-hidden="true" />
      <div>
        {before}
        <b>{word}</b>
        {sentence ? `: ${sentence}` : '.'} <span className="onb-shape-later">{later}</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ celebration --

/** Twelve coral dots burst from the centre and fade (0.9–1.9 s), then the coral button appears (rule 2). */
export function CelebrationBurst({ radius = 80 }: { radius?: number }) {
  return (
    <div className="onb-burst" aria-hidden="true" data-testid="onb-burst">
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * Math.PI * 2;
        const d = radius + (i % 3) * 18;
        const size = i % 2 ? 8 : 6;
        return (
          <span
            key={i}
            className="onb-burst-dot"
            style={{ width: size, height: size, ['--dx' as string]: `${Math.round(Math.cos(a) * d)}px`, ['--dy' as string]: `${Math.round(Math.sin(a) * d)}px` }}
          />
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------- helpers --

/** Seconds left, ticking once a second while `running`; restart(n) starts again. */
export function useCountdown(initial: number, running = true): [number, (n: number) => void] {
  const [left, setLeft] = useState(initial);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };
  const tick = () => {
    stop();
    timer.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          stop();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };
  const start = (n: number) => {
    stop();
    setLeft(n);
    if (n > 0 && running) tick();
  };
  useEffect(() => {
    if (running && left > 0 && !timer.current) tick();
    if (!running) stop();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);
  useEffect(() => {
    start(initial);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [left, start];
}

/** m:ss in Latin digits («0:45», board: LTR countdowns). */
export function clock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/** «+964 770 *** **67» — the masked number the code went to (Latin, rendered in an LTR isolate). */
export function maskedPhone(e164: string): string {
  const d = e164.replace(/^\+964/, '');
  if (d.length < 5) return e164;
  return `+964 ${d.slice(0, 3)} *** **${d.slice(-2)}`;
}

export const ROLE_LABEL: Record<string, string> = { owner: 'مالك', manager: 'مدير', staff: 'موظف' };

/** Arabic-Indic digits for a count the API gave. */
export function ar(n: number | string): string {
  return toArabicDigits(n);
}

/** Make `id` the active business, then open «اليوم» in it (an effect, never during render). */
export function EnterBusiness({ id }: { id: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    sessionStore.setBusinessId(id);
    navigate('/today', { replace: true });
  }, [id, navigate]);
  return null;
}

/** A labelled id for the hidden inputs the widgets own. */
export function useFieldId(): string {
  return useId();
}

/** Stop a keyboard event from reaching the shell's Enter handler (chips that toggle). */
export function stopEnter(e: KeyboardEvent<HTMLElement>): void {
  if (e.key === 'Enter') e.stopPropagation();
}
